import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { SelectChangeEvent } from "@mui/material/Select";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import { getFormattedDateTime } from "../../utils/getFormattedDateTime";
import LoadingCircle from "../../utils/LoadingCircle";

interface propsType {
  userState: userState;
}

interface Month {
  value: string;
  label: string;
}

interface RobotCleanerReservation {
  id: string;
  userId: string;
  reservDate: string;
  reservDt: string;
  inputDt: string;
  returnDueDate: string;
  deviceId: string;
  userAddr: string;
  userName: string;
  reservState: string;
  communityPrice: number;
}

const mainApi = new HttpMainApi();

function generateMonths() {
  const months: Month[] = [];
  const currentDate = new Date();
  for (let i = -1; i < 6; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    const yearMonth = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
    const formattedMonth = `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, "0")}월`;

    months.push({ value: yearMonth, label: formattedMonth });
  }

  return months;
}

function formatDateLabel(date: string) {
  if (!date || date.length !== 8) return date || "-";
  return `${date.slice(0, 4)}년 ${date.slice(4, 6)}월 ${date.slice(6, 8)}일`;
}

function formatDateTimeLabel(dateTime: string) {
  if (!dateTime || dateTime.length < 12) return dateTime || "-";
  return `${dateTime.slice(0, 4)}년 ${dateTime.slice(4, 6)}월 ${dateTime.slice(6, 8)}일 ${dateTime.slice(
    8,
    10,
  )}:${dateTime.slice(10, 12)}`;
}

function formatReturnDateLabel(reservDate: string) {
  if (!reservDate || reservDate.length !== 8) return "-";
  const returnDate = new Date(
    Number(reservDate.slice(0, 4)),
    Number(reservDate.slice(4, 6)) - 1,
    Number(reservDate.slice(6, 8)) + 6,
  );
  return `${returnDate.getFullYear()}년 ${String(returnDate.getMonth() + 1).padStart(2, "0")}월 ${String(
    returnDate.getDate(),
  ).padStart(2, "0")}일 17:00`;
}

function parseAddress(address = "") {
  const normalized = address.trim().replace(/\s+/g, " ");
  const match = normalized.match(/^(\d+)\s*동?\s+(\d+)\s*호?$/);

  if (!match) {
    return {
      formattedAddress: normalized || "-",
    };
  }

  const dong = `${match[1]}동`;
  const ho = `${match[2].padStart(4, "0")}호`;

  return {
    formattedAddress: `${dong} ${ho}`,
  };
}

function normalizeReservation(row: any, index: number): RobotCleanerReservation {
  const { formattedAddress } = parseAddress(row.userAddr);

  return {
    id: row.reservationId || `${row.reservDate || "no-date"}-${row.userId || "no-user"}-${index}`,
    userId: row.userId || "",
    reservDate: row.reservDate || "",
    reservDt: row.reservDt || formatDateLabel(row.reservDate),
    inputDt: formatDateTimeLabel(row.inputDt),
    returnDueDate: row.returnDueDate || row.returnDt || formatReturnDateLabel(row.reservDate),
    deviceId: row.deviceId || row.deviceNum || "-",
    userAddr: formattedAddress,
    userName: row.userName || "-",
    reservState: row.reservState || "-",
    communityPrice: Number(row.communityPrice || 0),
  };
}

const RobotCleanerRentalStatus = (_props: propsType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectMonth, setSelectMonth] = useState("");
  const [months, setMonths] = useState<Month[]>([]);
  const [reservations, setReservations] = useState<RobotCleanerReservation[]>([]);

  useEffect(() => {
    const generatedMonths = generateMonths();
    setMonths(generatedMonths);
    if (generatedMonths.length > 0) {
      setSelectMonth(generatedMonths[1].value);
    }
  }, []);

  const getReservationList = async () => {
    setIsLoading(true);
    setReservations([]);

    try {
      const res = await mainApi.get_robot_cleaner_month_reservation({
        month: selectMonth,
      });

      if (res.code === "200") {
        setReservations(res.response.result.map(normalizeReservation));
      } else if (res.code === "400") {
        setReservations([]);
      } else {
        alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    setSelectMonth(event.target.value as string);
  };

  const totalPrice = useMemo(
    () => reservations.reduce((acc, reservation) => acc + reservation.communityPrice, 0),
    [reservations],
  );

  const forceCancelReservation = async (reservation: RobotCleanerReservation) => {
    if (!reservation.userId) {
      alert("예약자 정보를 확인할 수 없어 취소할 수 없습니다.");
      return;
    }
    if (!window.confirm("비/노쇼 등의 사유로 예약을 강제 취소하시겠습니까?")) return;

    setIsLoading(true);
    try {
      const res = await mainApi.put_cancel_robot_cleaner_reservation({
        reservDate: reservation.reservDate,
        reservationId: reservation.id,
        userId: reservation.userId,
        inputDt: getFormattedDateTime().compactDateTime,
        adminMode: true,
      });

      if (res.code === "200") {
        alert("예약이 취소되었습니다.");
        await getReservationList();
      } else {
        alert(`예약 취소 중 오류가 발생했습니다.\n- CODE : ${res.code}`);
      }
    } catch (error) {
      alert(`예약 취소 중 오류가 발생했습니다.\n${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "reservDt",
      headerName: "예약일",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography component="span" style={{ color: "#d32f2f", fontWeight: 700 }}>
          {params.row.reservDt}
        </Typography>
      ),
    },
    {
      field: "returnDueDate",
      headerName: "반납 예정",
      width: 170,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "deviceId",
      headerName: "장비",
      width: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "userAddr",
      headerName: "주소",
      width: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "userName",
      headerName: "예약자",
      width: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "inputDt",
      headerName: "예약 시간",
      width: 170,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "reservState",
      headerName: "상태",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "communityPrice",
      headerName: "대여료",
      width: 110,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row.communityPrice || 0,
    },
    {
      field: "cancel",
      headerName: "예약 취소",
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={params.row.reservState !== "신청"}
          onClick={() => forceCancelReservation(params.row as RobotCleanerReservation)}
        >
          취소
        </Button>
      ),
    },
  ];

  return (
    <>
      <Stack direction="column" spacing={2} sx={{ p: "32px", pb: "16px !important" }}>
        <Box sx={{ position: "absolute", top: "60px", right: "20px" }}>
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <Box sx={{ minWidth: 150 }}>
              <FormControl fullWidth>
                <InputLabel id="robot-cleaner-rental-status-month-select-label">기준</InputLabel>
                <Select
                  labelId="robot-cleaner-rental-status-month-select-label"
                  id="robot-cleaner-rental-status-month-select"
                  value={selectMonth}
                  label="기준"
                  onChange={handleMonthChange}
                  sx={{ height: 46 }}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Button
              variant="contained"
              size="small"
              color="primary"
              aria-label="조회"
              onClick={getReservationList}
              sx={{
                borderRadius: "4px",
                width: "54px",
                height: "54px",
                marginTop: "9px !important",
              }}
            >
              <SearchIcon />
            </Button>
          </Stack>
        </Box>
        <Box className="sub-header-root" sx={{ marginBottom: 4 }}>
          <Typography variant="h5" gutterBottom className="sub-header-title">
            청소 로봇 대여 현황
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid>
            <DataGrid
              rows={reservations}
              columns={columns}
              disableRowSelectionOnClick
              style={{ height: 700 }}
              getRowId={(row) => row.id}
            />
            {!isLoading && reservations.length === 0 && (
              <Typography color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
                조회된 청소 로봇 대여 예약이 없습니다.
              </Typography>
            )}
            <Grid item xs={12} sm={12} md={6} lg={4} xl={3}></Grid>
          </Grid>
          <div style={{ marginTop: "20px" }}>총 대여료: {totalPrice.toLocaleString()} 원</div>
        </Box>

      </Stack>
      <LoadingCircle loading={isLoading} />
    </>
  );
};

export default RobotCleanerRentalStatus;
